/**
 * OTP Verification Page
 *
 * Security Design:
 * - OTP is sent to a SEPARATE channel (email inbox) -- never shown directly
 * - User must open the Email Inbox panel to get the code
 * - This implements genuine two-step verification
 * - After verification, decryption happens 100% client-side
 */

import React, { useState, useEffect, useRef } from 'react';
import { requestOTP, verifyOTP, downloadEncryptedFile, getFileMetadata, getEmails, markEmailRead, logClientDecryptComplete } from '../services/api';
import { decryptFile } from '../services/encryption';

function OTPVerificationPage({ fileId, onVerified, onCancel }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('sending');
  const [message, setMessage] = useState('');
  const [expiresIn, setExpiresIn] = useState(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [integrityHash, setIntegrityHash] = useState(null);
  const [originalHash, setOriginalHash] = useState(null);
  const [hashMatch, setHashMatch] = useState(null);
  const [showInbox, setShowInbox] = useState(false);
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const otpSentRef = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (otpSentRef.current) return;
    otpSentRef.current = true;
    sendOTP();
    getFileMetadata(fileId).then(setFileInfo).catch(() => {});
  }, [fileId]);

  const sendOTP = async () => {
    setStatus('sending');
    setMessage('Sending verification code to your email...');
    try {
      const response = await requestOTP(fileId);
      setExpiresIn(response.expires_in_minutes);
      setStatus('ready');
      setMessage(response.message || 'Verification code sent. Open your Email Inbox below.');
    } catch (err) {
      setStatus('ready');
      setMessage('Enter the 6-digit code from your Email Inbox.');
      setExpiresIn(5);
    }
  };

  const loadEmails = async () => {
    try {
      const data = await getEmails();
      setEmails(data);
      if (!selectedEmail && data.length > 0) setSelectedEmail(data[0]);
    } catch {}
  };

  const openInbox = () => { setShowInbox(true); loadEmails(); };

  const handleSelectEmail = async (email) => {
    setSelectedEmail(email);
    if (!email.is_read) { try { await markEmailRead(email.id); } catch {} }
  };

  const extractOTP = (body) => {
    const match = body.match(/(\d{6})/);
    return match ? match[1] : null;
  };

  const useThisCode = (code) => { setOtpCode(code); setShowInbox(false); };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return d.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) { setMessage('Please enter a valid 6-digit code.'); return; }
    setStatus('verifying');
    setMessage('Verifying code...');
    try {
      const response = await verifyOTP(fileId, otpCode);
      if (response.verified) {
        setOtpVerified(true);
        setFileInfo(prev => ({
          ...prev,
          iv: response.iv || prev?.iv,
          salt: response.salt || prev?.salt,
        }));
        setStatus('verified');
        setMessage('OTP verified! Enter your encryption password to decrypt the file.');
      }
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Invalid code. Please try again.');
    }
  };

  const handleDecryptAndDownload = async (e) => {
    e.preventDefault();
    if (!otpVerified) {
      setMessage('Please verify your OTP before downloading.');
      return;
    }
    if (!password) { setMessage('Please enter your encryption password.'); return; }
    setStatus('decrypting');
    setMessage('Downloading encrypted file...');
    try {
      const encryptedData = await downloadEncryptedFile(fileId);
      setMessage('Decrypting file locally in your browser...');
      const fileHash = fileInfo?.file_hash;
      // Use backend salt (from DB) as primary, localStorage as fallback
      const salt = fileInfo?.salt || localStorage.getItem(`cdeaas_salt_${fileHash}`);
      if (!salt) {
        setStatus('error');
        setMessage('Encryption salt not found. The file may have been uploaded before salt was stored server-side.');
        return;
      }
      // Use the file's security level parameters for decryption
      // Priority: localStorage (most reliable) > backend metadata > defaults
      const localKeyLength = localStorage.getItem(`cdeaas_keylength_${fileHash}`);
      const localIterations = localStorage.getItem(`cdeaas_iterations_${fileHash}`);
      const keyLength = localKeyLength ? Number(localKeyLength)
        : (fileInfo?.encryption_algorithm === 'AES-128-GCM' ? 128 : 256);
      const iterations = localIterations ? Number(localIterations)
        : (fileInfo?.pbkdf2_iterations || 100000);
      const decryptedData = await decryptFile(encryptedData, password, fileInfo.iv, salt, keyLength, iterations);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', decryptedData);
      const hashHex = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
      setIntegrityHash(hashHex);

      // Retrieve original plaintext hash for auto-comparison
      const storedOriginalHash = localStorage.getItem(`cdeaas_originalhash_${fileHash}`);
      if (storedOriginalHash) {
        setOriginalHash(storedOriginalHash);
        setHashMatch(hashHex === storedOriginalHash);
      }
      const blob = new Blob([decryptedData]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileInfo?.original_filename || 'decrypted_file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setStatus('done');
      setMessage('File decrypted locally and downloaded securely. The server never accessed your file content.');
      try {
        await logClientDecryptComplete(fileId, 'owner');
      } catch {
        /* audit is best-effort; decryption already succeeded */
      }
    } catch (err) {
      setStatus('error');
      setMessage(err.message?.includes('decrypt') ? 'Decryption failed. Wrong password or corrupted file.' : (err.message || 'Failed.'));
    }
  };

  const statusColor = status === 'error' ? { bg: 'var(--red-light)', color: 'var(--red)', border: '#fecaca' }
    : status === 'done' ? { bg: 'var(--green-light)', color: 'var(--green)', border: '#bbf7d0' }
    : { bg: 'var(--blue-light)', color: 'var(--blue)', border: '#bfdbfe' };
  return (
    <div
      className="page-container admin-page-shell admin-tactile-page"
      style={{
        ...st.container,
        gap: isMobile ? 14 : st.container.gap,
      }}
    >
      {/* Main OTP Card */}
      <div
        style={{
          ...st.card,
          padding: isMobile ? 16 : st.card.padding,
          borderRadius: isMobile ? 16 : st.card.borderRadius,
          maxWidth: isMobile ? '100%' : st.card.maxWidth,
        }}
      >
        <h2 style={{ ...st.title, fontSize: isMobile ? 20 : st.title.fontSize }}>Two-Step Verification</h2>
        <p style={st.subtitle}>
          A verification code has been sent to your email.
          Click the button below to open your inbox and get the code.
        </p>

        <div style={st.securityInfo}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <div>
            <strong>Why two-step?</strong>
            <p style={{ margin: '2px 0 0', fontSize: 12 }}>Even if someone gains access to your account, they
              cannot decrypt your files without also accessing your email for the OTP code.</p>
          </div>
        </div>

        {/* Status */}
        <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, marginBottom: 12, backgroundColor: statusColor.bg, color: statusColor.color, border: `1px solid ${statusColor.border}` }}>
          {message}
        </div>

        {/* Open Inbox Button */}
        {!otpVerified && status !== 'sending' && (
          <button style={st.inboxBtn} onClick={openInbox} type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            Open Email Inbox to Get Code
          </button>
        )}

        {/* Step 1: OTP Input */}
        {(status === 'ready' || status === 'error') && !otpVerified && (
          <form onSubmit={handleVerify} style={st.form}>
            <div style={st.otpContainer}>
              <input
                type="text" value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{
                  ...st.otpInput,
                  width: isMobile ? '100%' : st.otpInput.width,
                  maxWidth: isMobile ? '100%' : st.otpInput.width,
                  fontSize: isMobile ? 26 : st.otpInput.fontSize,
                  letterSpacing: isMobile ? 8 : st.otpInput.letterSpacing,
                  padding: isMobile ? '12px 16px' : st.otpInput.padding,
                }}
                placeholder="000000" maxLength={6} autoFocus
              />
            </div>
            {expiresIn && <p style={st.expiryText}>Code expires in {expiresIn} minutes</p>}
            <div style={st.actions}>
              <button type="submit" style={{ ...st.primaryBtn, width: isMobile ? '100%' : 'auto' }}>Verify Code</button>
              <button type="button" style={{ ...st.outlineBtn, width: isMobile ? '100%' : 'auto' }} onClick={() => { otpSentRef.current = false; sendOTP(); }}>Resend Code</button>
              <button type="button" style={{ ...st.ghostBtn, width: isMobile ? '100%' : 'auto' }} onClick={onCancel}>Cancel</button>
            </div>
          </form>
        )}

        {/* Step 2: Password */}
        {(status === 'verified' || (status === 'error' && otpVerified)) && (
          <form onSubmit={handleDecryptAndDownload} style={st.form}>
            <div style={st.localCopyWarning}>
              After you decrypt, the plaintext saves to your device like any normal download. Access expiry on the server does not remove or control that local copy — you are responsible for protecting it.
            </div>
            <div style={st.passwordSection}>
              <label style={st.label}>Encryption Password</label>
              <div className="password-field">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  style={{ ...st.passwordInput, fontSize: isMobile ? 14 : st.passwordInput.fontSize, paddingRight: isMobile ? 36 : st.passwordInput.paddingRight }}
                  placeholder="Enter the password you used to encrypt this file" autoFocus />
                <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              <p style={st.hint}>Decryption happens locally in your browser. The server never sees your password.</p>
            </div>
            <div style={st.actions}>
              <button type="submit" style={{ ...st.primaryBtn, width: isMobile ? '100%' : 'auto' }}>Decrypt & Download</button>
              <button type="button" style={{ ...st.ghostBtn, width: isMobile ? '100%' : 'auto' }} onClick={onCancel}>Cancel</button>
            </div>
          </form>
        )}

        {status === 'sending' && <p style={st.loadingText}>Sending verification code...</p>}
        {status === 'verifying' && <p style={st.loadingText}>Verifying OTP...</p>}
        {status === 'decrypting' && <p style={st.loadingText}>Decrypting file...</p>}

        {/* Success */}
        {status === 'done' && (
          <div style={st.successBox}>
            <div style={st.successIcon}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <p style={{ fontWeight: 600, fontSize: 16, margin: '10px 0 4px', color: 'var(--text-primary)' }}>File Decrypted Successfully</p>
            {integrityHash && (
              <div style={st.integrityBox}>
                <strong>File Integrity Verification</strong>

                {/* Auto-comparison result */}
                {hashMatch !== null && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 12px', borderRadius: 'var(--radius-sm)', margin: '10px 0',
                    backgroundColor: hashMatch ? '#dcfce7' : '#fef2f2',
                    border: hashMatch ? '1px solid #86efac' : '1px solid #fecaca',
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                      stroke={hashMatch ? 'var(--green)' : 'var(--red)'} strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round">
                      {hashMatch
                        ? <polyline points="20 6 9 17 4 12"/>
                        : <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                      }
                    </svg>
                    <span style={{
                      fontSize: 14, fontWeight: 700,
                      color: hashMatch ? 'var(--green)' : 'var(--red)',
                    }}>
                      {hashMatch ? 'INTEGRITY VERIFIED -- Hashes Match' : 'WARNING -- Hash Mismatch! File may be corrupted.'}
                    </span>
                  </div>
                )}

                {/* Hash details */}
                <div style={{ marginTop: 8 }}>
                  {originalHash && (
                    <>
                      <p style={{ fontSize: 12, margin: '0 0 2px', color: 'var(--text-muted)', fontWeight: 600 }}>Original File Hash (before encryption):</p>
                      <code style={st.hashCode}>{originalHash}</code>
                    </>
                  )}
                  <p style={{ fontSize: 12, margin: '8px 0 2px', color: 'var(--text-muted)', fontWeight: 600 }}>Decrypted File Hash (after decryption):</p>
                  <code style={st.hashCode}>{integrityHash}</code>
                </div>

                {!originalHash && (
                  <p style={{ fontSize: 11, color: 'var(--green)', margin: '8px 0 0' }}>
                    Original hash not available (file was encrypted on a different device or browser).
                    You can manually compare using an external SHA-256 tool.</p>
                )}
              </div>
            )}
            <button type="button" className="saas-back-btn" style={{ marginTop: 16 }} onClick={() => onVerified()}>
              <span className="saas-back-btn__icon" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
              </span>
              Back to My Files
            </button>
          </div>
        )}
      </div>

      {/* Security Flow Sidebar */}
      <div
        style={{
          ...st.flowCard,
          width: isMobile ? '100%' : st.flowCard.width,
          padding: isMobile ? 16 : st.flowCard.padding,
          borderRadius: isMobile ? 16 : st.flowCard.borderRadius,
        }}
      >
        <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Security Flow</h3>
        {[
          { step: '1', label: 'Login', desc: 'Firebase Auth', done: true },
          { step: '2', label: 'Request OTP', desc: 'Code sent to email', done: status !== 'sending' },
          { step: '3', label: 'Check Email', desc: 'Get code from inbox', done: otpVerified },
          { step: '4', label: 'Verify OTP', desc: 'Server validates hash', done: otpVerified },
          { step: '5', label: 'Decrypt', desc: 'Client-side AES-256', done: status === 'done' },
          { step: '6', label: 'Integrity', desc: 'SHA-256 verified', done: status === 'done' },
        ].map((item, i) => (
          <div key={i} style={st.flowStep}>
            <div style={{
              ...st.flowCircle,
              backgroundColor: item.done ? 'var(--green)' : 'var(--border)',
              color: item.done ? '#fff' : 'var(--text-muted)',
            }}>
              {item.done ? '\u2713' : item.step}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: item.done ? 'var(--green)' : 'var(--text-body)' }}>{item.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{item.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ===== INLINE EMAIL INBOX MODAL ===== */}
      {showInbox && (
        <div style={st.modalOverlay} onClick={() => setShowInbox(false)}>
          <div style={st.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={st.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Email Inbox</h3>
              <button style={st.modalClose} onClick={() => setShowInbox(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div style={st.modalBody}>
              {/* Email list */}
              <div style={st.modalEmailList}>
                {emails.length === 0 ? (
                  <p style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                    Loading emails... If empty, click "Resend Code".
                  </p>
                ) : (
                  emails.map((email) => {
                    const code = extractOTP(email.body);
                    return (
                      <div key={email.id}
                        style={{
                          ...st.modalEmailItem,
                          backgroundColor: selectedEmail?.id === email.id ? 'var(--blue-light)' : 'var(--bg-card)',
                          borderLeft: email.is_read ? '3px solid transparent' : '3px solid var(--blue)',
                        }}
                        onClick={() => handleSelectEmail(email)}
                      >
                        <div style={{ fontSize: 11, color: 'var(--blue)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>JOSMY Security</div>
                        <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: email.is_read ? 400 : 600, marginTop: 2 }}>{email.subject}</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{formatDate(email.created_at)}</span>
                          {code && (
                            <button style={st.useCodeBtn} onClick={(e) => { e.stopPropagation(); useThisCode(code); }}>
                              Use Code: {code}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Selected email detail */}
              {selectedEmail && (
                <div style={st.modalEmailDetail}>
                  <h4 style={{ margin: '0 0 4px', fontSize: 15, color: 'var(--text-primary)' }}>{selectedEmail.subject}</h4>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                    From: security@cdeaas.cloud &middot; {formatDate(selectedEmail.created_at)}
                  </p>
                  <pre style={{ fontFamily: 'inherit', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: '0 0 16px', color: 'var(--text-body)' }}>
                    {selectedEmail.body}
                  </pre>
                  {extractOTP(selectedEmail.body) && (
                    <div style={st.modalOtpBox}>
                      <span style={{ fontSize: 28, fontWeight: 700, fontFamily: 'monospace', letterSpacing: 8, color: 'var(--text-primary)' }}>
                        {extractOTP(selectedEmail.body)}
                      </span>
                      <button style={st.useCodeBtnLarge} onClick={() => useThisCode(extractOTP(selectedEmail.body))}>
                        Use This Code
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const st = {
  container: { display: 'flex', gap: 28, justifyContent: 'center', paddingTop: 10, flexWrap: 'wrap' },
  card: {
    backgroundColor: 'var(--surface-85)', borderRadius: 24, padding: 36,
    width: '100%', maxWidth: 520, border: '1.5px solid rgba(99,102,241,0.08)',
    boxShadow: '0 4px 24px rgba(99,102,241,0.06)', backdropFilter: 'blur(12px)',
  },
  flowCard: {
    backgroundColor: 'var(--surface-85)', borderRadius: 20, padding: 24,
    width: 230, border: '1.5px solid rgba(99,102,241,0.08)', alignSelf: 'flex-start',
    boxShadow: '0 4px 20px rgba(99,102,241,0.06)', backdropFilter: 'blur(12px)',
  },
  title: {
    fontSize: 22, fontWeight: 900, margin: '0 0 8px 0', letterSpacing: '-0.4px',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: 13, color: 'var(--text-muted)', margin: '0 0 18px 0', lineHeight: 1.5, fontWeight: 500 },
  securityInfo: {
    background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.03))',
    padding: 14, borderRadius: 16,
    fontSize: 13, color: 'var(--text-faint)', marginBottom: 18, lineHeight: 1.4,
    display: 'flex', gap: 10, alignItems: 'flex-start',
    border: '1.5px solid rgba(99,102,241,0.08)',
  },
  inboxBtn: {
    width: '100%', padding: '13px 18px',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff',
    border: 'none', borderRadius: 14, cursor: 'pointer', fontSize: 14,
    fontWeight: 700, marginBottom: 18, display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 8,
    boxShadow: '0 4px 14px rgba(99,102,241,0.3)', transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 18 },
  otpContainer: { display: 'flex', justifyContent: 'center' },
  otpInput: {
    fontSize: 30, textAlign: 'center', letterSpacing: 10, padding: '14px 22px',
    border: '2px solid rgba(99,102,241,0.15)', borderRadius: 16, width: 230,
    outline: 'none', fontFamily: 'monospace', backgroundColor: 'var(--surface-90)',
    transition: 'all 0.2s ease', boxShadow: '0 2px 8px rgba(99,102,241,0.06)',
  },
  expiryText: { fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', margin: 0, fontWeight: 500 },
  actions: { display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' },
  primaryBtn: {
    padding: '12px 26px', background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff',
    border: 'none', borderRadius: 14, cursor: 'pointer', fontSize: 14, fontWeight: 700,
    boxShadow: '0 3px 12px rgba(99,102,241,0.3)', transition: 'all 0.3s ease',
  },
  outlineBtn: {
    padding: '12px 22px', backgroundColor: 'transparent', color: 'var(--text-body)',
    border: '1.5px solid rgba(99,102,241,0.12)', borderRadius: 14, cursor: 'pointer',
    fontSize: 13, fontWeight: 600, transition: 'all 0.2s ease',
  },
  ghostBtn: {
    padding: '12px 22px', backgroundColor: 'transparent', color: 'var(--text-secondary)',
    border: '1.5px solid rgba(99,102,241,0.08)', borderRadius: 14, cursor: 'pointer',
    fontSize: 13, transition: 'all 0.2s ease',
  },
  loadingText: { textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 500 },
  localCopyWarning: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
    padding: '12px 14px',
    borderRadius: 14,
    background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))',
    border: '1px solid rgba(245,158,11,0.25)',
    marginBottom: 4,
  },
  passwordSection: { display: 'flex', flexDirection: 'column', gap: 8 },
  label: { fontSize: 12, fontWeight: 700, color: 'var(--text-body)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  passwordInput: {
    padding: '12px 14px', border: '1.5px solid rgba(99,102,241,0.1)', borderRadius: 14,
    fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box',
    backgroundColor: 'var(--surface-90)', transition: 'all 0.2s ease',
  },
  hint: { fontSize: 12, color: 'var(--text-secondary)', margin: 0 },
  successBox: { textAlign: 'center', padding: '20px 0' },
  successIcon: {
    width: 60, height: 60, borderRadius: '50%',
    background: 'linear-gradient(135deg, #22C55E, #16A34A)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto',
    boxShadow: '0 4px 16px rgba(34,197,94,0.3)',
  },
  integrityBox: {
    background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(22,163,74,0.04))',
    padding: 16, borderRadius: 16,
    textAlign: 'left', marginTop: 18, fontSize: 13, color: 'var(--green)',
    border: '1.5px solid rgba(34,197,94,0.15)',
  },
  hashCode: {
    display: 'block', fontSize: 10, fontFamily: 'monospace', wordBreak: 'break-all',
    padding: 8,
    background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(139,92,246,0.02))',
    borderRadius: 10, marginTop: 6,
    color: 'var(--text-body)', border: '1px solid rgba(99,102,241,0.06)',
  },
  flowStep: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18 },
  flowCircle: {
    width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0,
    boxShadow: '0 2px 8px rgba(99,102,241,0.1)',
  },

  // Modal
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15,16,40,0.5)', display: 'flex',
    justifyContent: 'center', alignItems: 'center', zIndex: 1000,
    backdropFilter: 'blur(6px)',
  },
  modalContent: {
    backgroundColor: 'var(--surface-95)', borderRadius: 24, width: '90%', maxWidth: 720,
    maxHeight: '80vh', overflow: 'hidden', border: '1.5px solid rgba(99,102,241,0.1)',
    boxShadow: '0 24px 80px rgba(99,102,241,0.18), 0 8px 24px rgba(0,0,0,0.08)',
    backdropFilter: 'blur(20px)',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 22px', borderBottom: '1px solid rgba(99,102,241,0.08)',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff',
  },
  modalClose: {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer',
    padding: '4px', display: 'flex', alignItems: 'center',
  },
  modalBody: { display: 'flex', flexDirection: 'column', maxHeight: 'calc(80vh - 56px)', overflowY: 'auto' },
  modalEmailList: { borderBottom: '1px solid rgba(99,102,241,0.06)' },
  modalEmailItem: {
    padding: '14px 18px', cursor: 'pointer', borderBottom: '1px solid rgba(99,102,241,0.04)',
    transition: 'background-color 0.2s ease',
  },
  modalEmailDetail: { padding: 22 },
  modalOtpBox: {
    display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'center',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.04))',
    padding: '18px 22px', borderRadius: 16,
    border: '2px solid rgba(99,102,241,0.15)',
  },
  useCodeBtn: {
    padding: '6px 14px', background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff',
    border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 700,
    boxShadow: '0 2px 8px rgba(99,102,241,0.25)',
  },
  useCodeBtnLarge: {
    padding: '12px 26px', background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff',
    border: 'none', borderRadius: 14, cursor: 'pointer', fontSize: 14, fontWeight: 700,
    boxShadow: '0 3px 12px rgba(99,102,241,0.3)', transition: 'all 0.3s ease',
  },
};

export default OTPVerificationPage;
